import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listTrips, updateTripStatus, type Trip } from '@/services/tripService'
import { signOut } from '@/services/authService'

function tripDayCount(trip: Trip): number {
  const start = new Date(`${trip.start_date}T00:00:00Z`)
  const end = new Date(`${trip.end_date}T00:00:00Z`)
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
}

function TripCard({ trip }: { trip: Trip }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isPast = trip.end_date < new Date().toISOString().slice(0, 10)

  const archiveMutation = useMutation({
    mutationFn: () => updateTripStatus(trip.id, 'archived'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['trips'] })
      await queryClient.invalidateQueries({ queryKey: ['archivedTrips'] })
    },
  })

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-panel text-left">
      <button
        type="button"
        onClick={() => navigate(`/trips/${trip.id}/itinerary`)}
        className="block w-full text-left"
      >
        <div
          className="relative flex h-32 items-start justify-end p-3"
          style={{ background: trip.cover_url ?? undefined }}
        >
          <span
            className={
              'rounded-full px-2.5 py-1 font-mono text-[10px] tracking-wide uppercase ' +
              (isPast ? 'bg-black/35 text-mist' : 'bg-brass text-night')
            }
          >
            {isPast ? 'Passé' : 'À venir'}
          </span>
        </div>
        <div className="p-5 pb-0">
          <h3 className="font-display text-xl text-ivory">{trip.title}</h3>
          <div className="mb-3 mt-1 text-sm text-mist">{trip.destination}</div>
        </div>
      </button>
      <div className="flex items-center justify-between border-t border-line px-5 py-3">
        <span className="font-mono text-xs text-brass-dim">
          {trip.start_date} – {trip.end_date}
        </span>
        <span className="font-mono text-xs text-mist">{tripDayCount(trip)} j</span>
      </div>
      <div className="border-t border-line px-5 py-2.5">
        <button
          type="button"
          onClick={() => archiveMutation.mutate()}
          disabled={archiveMutation.isPending}
          className="font-sans text-xs text-mist hover:text-brass-warm"
        >
          {t('trips.archive')}
        </button>
      </div>
    </div>
  )
}

export function TripsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: trips, isLoading } = useQuery({ queryKey: ['trips'], queryFn: listTrips })

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-night px-12 py-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-2 font-mono text-xs tracking-[0.22em] text-brass-dim uppercase">
            {t('trips.kicker')}
          </div>
          <h1 className="font-display text-4xl text-ivory">{t('trips.title')}</h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/trips/archived')}
            className="font-sans text-sm text-mist hover:text-ivory"
          >
            {t('trips.archived')}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="font-sans text-sm text-mist hover:text-ivory"
          >
            {t('home.logout')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/trips/new')}
            className="rounded border border-brass bg-brass px-5 py-2.5 font-sans text-sm font-medium text-night"
          >
            {t('trips.newTrip')}
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-mist">{t('trips.loading')}</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
          {trips?.map((trip) => (
            <TripCard key={trip.id} trip={trip} />
          ))}

          <button
            type="button"
            onClick={() => navigate('/trips/new')}
            className="flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-line-strong text-mist hover:border-brass hover:text-brass"
          >
            <span className="font-display text-3xl">+</span>
            <span className="font-sans text-sm">{t('trips.startOdyssey')}</span>
          </button>
        </div>
      )}
    </div>
  )
}
